import * as _ from 'lodash';
import * as PIXI from 'pixi.js';

import CookieData from '@/assets/js/CookieData';
import SoundController from '@/assets/js/SoundController';
import Utils from '@/assets/js/Utils';
import Background from '@/assets/js/game/Background';
import MutekiAnimation from '@/assets/js/game/MutekiAnimation';
import Map from '@/assets/js/game/Map';
import Player from '@/assets/js/game/Player';
import TextureManager from '@/assets/js/game/TextureManager';
import ScoreAnimation from '@/assets/js/game/ScoreAnimation';

const GAME_WIDTH = 640;
const GAME_HEIGHT = 800;
const MAX_LIFE = 3;
const MAP_AREA_LENGTH = 20;
const RUN_SPEED = 6.6;

let element, ui, app, stage, status;

export default class GameManager {
  static init(parentElement, parentUI) {
    status = {
      isStart: false,
      isPause: false,
      isGoal: false,
      score: 0,
      life: MAX_LIFE
    };

    element = parentElement;
    ui = parentUI;

    TextureManager.load(() => {
      GameManager.setup(element);
      ui.start();
    });
  }

  static setup(element) {
    app = new PIXI.Application({
      antialias: true,
      width: element.clientWidth,
      height: element.clientHeight,
      resizeTo: element
    });
    element.appendChild(app.view);

    stage = new PIXI.Container();
    app.stage.addChild(stage);

    Background.init(stage);
    Map.init(stage);
    Player.init(stage);
    MutekiAnimation.init(stage);
    ScoreAnimation.init(stage);

    app.ticker.add(GameManager.renderer);

    const gameview = element.parentNode;
    gameview.addEventListener('touchstart', GameManager.onTouch, false);
    gameview.addEventListener('click', GameManager.onTouch, false);
    window.addEventListener('resize', GameManager.onResize);
  }

  static reset() {
    app.start();
    status.isStart = false;
    status.isPause = false;
    status.isGoal = false;
    status.score = 0;
    status.life = MAX_LIFE;
    Player.reset();
    Background.reset();
    Map.reset();
  }

  static renderer(delta) {
    Background.update();
    Player.update(delta);
    MutekiAnimation.update(delta);
    ScoreAnimation.update(delta);
    Map.update(delta);
  }

  static start() {
    status.isStart = true;
  }

  static standby() {
    Player.standby();
  }

  static pause() {
    status.isPause = true;
    Player.pause();
    app.stop();
  }

  static resume() {
    status.isPause = false;
    Player.resume();
    app.start();
  }

  static powder250() {
    Player.pause();
    app.stop();
    SoundController.play('se_player_muteki');

    ui.powder250(() => {
      Player.resume();
      app.start();
      Player.muteki();
    });
  }

  static absorb() {
    Player.absorb();
  }

  static goal() {
    status.isGoal = true;
    Background.stop();
    Player.goal(() => ui.goal());

    SoundController.stopAll();
    SoundController.play('se_shot_handgun');
    Utils.wait(200).then(() => {
      SoundController.play('se_shot_handgun');
    });
    Utils.wait(700).then(() => {
      SoundController.play('jingle_goal');
    });
  }

  static beforeDestroy() {
    if (app) app.ticker.remove(GameManager.renderer);
    Player.beforeDestroy();
    element.removeEventListener('touchstart', GameManager.onTouch);
    element.removeEventListener('click', GameManager.onTouch);
    window.removeEventListener('resize', GameManager.onResize);
  }

  static onResize() {
    Map.onResize();
    Player.onResize();
  }

  static onTouch() {
    if (status.isPause || status.isGoal) return;
    Player.jump();
  }

  static calcRatio(number) {
    return number * (app.view.width / GameManager.gameWidth);
  }

  static addScore(plus) {
    status.score += plus;
    ui.updateScore(status.score);
  }

  static addLife() {
    status.life++;
    if (status.life > GameManager.maxLife) {
      status.life = GameManager.maxLife;
      return;
    }
    ui.addLife();
  }

  static hit() {
    if (Player.isMuteki || Player.isHit) return;
    Player.hit();

    status.life--;
    ui.removeLife();
    if (status.life <= 0) {
      app.stop();
      ui.gameOver();
    }
  }

  static get isStart() {
    return status.isStart;
  }

  static get isGoal() {
    return status.isGoal;
  }

  static get gameWidth() {
    return GAME_WIDTH;
  }

  static get gameHeight() {
    return GAME_HEIGHT;
  }

  static get maxLife() {
    return MAX_LIFE;
  }

  static get mapAreaLength() {
    return MAP_AREA_LENGTH;
  }

  static get runSpeed() {
    return RUN_SPEED;
  }

  static get recordList() {
    return [
      CookieData.highScore >= 529,
      CookieData.highScore >= 800,
      CookieData.totalHappyTurn >= 300,
      CookieData.totalHappyTurn >= 1000,
      CookieData.totalHappyTurn >= 2500,
      CookieData.totalHappyTurn >= 5000
    ];
  }

  static get level() {
    let level = 1;
    _.forEach(GameManager.recordList, record => {
      if (record) level++;
    });
    return level;
  }

  static debugFinish() {
    Map.debugFinish();
  }
}
